// The PA Dealing wire vocabulary and DTOs (SEP-264).
//
// The projection layer never imports the ontology (ADR-0003), so the closed
// sets below repeat `vendor/septima-ontology-wealth/packages/ontology-wealth/src/pa-dealing/types.ts`. A
// server test pins the two copies to each other, so a new value in one place
// fails the build until it lands in the other.
//
// Two shapes serve two readers. The EMPLOYEE shape holds the request as its
// owner sees it: no rule results, no match evidence, no restriction field, no
// internal comment. The REVIEW shape is the full record for Compliance.

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const paTradeRequestStatusSchema = z.enum([
    'draft',
    'pending_compliance',
    'information_requested',
    'auto_cleared',
    'approved',
    'rejected',
    'withdrawn',
    'expired',
    'blocked_restricted',
]);
export type PaTradeRequestStatus = z.infer<typeof paTradeRequestStatusSchema>;

export const paTransactionTypeSchema = z.enum(['market', 'limit']);
export type PaTransactionType = z.infer<typeof paTransactionTypeSchema>;

export const paSideSchema = z.enum(['buy', 'sell']);
export type PaSide = z.infer<typeof paSideSchema>;

export const paSecurityTypeSchema = z.enum([
    'equity',
    'bond',
    'fund_or_etf',
    'warrant_or_option',
    'other',
]);
export type PaSecurityType = z.infer<typeof paSecurityTypeSchema>;

export const paBeneficialOwnerSchema = z.enum(['self', 'other']);
export type PaBeneficialOwner = z.infer<typeof paBeneficialOwnerSchema>;

export const paSecuritySourceSchema = z.enum(['search', 'manual']);
export type PaSecuritySource = z.infer<typeof paSecuritySourceSchema>;

export const paQuestionnaireKindSchema = z.enum(['im', 'non_im']);
export type PaQuestionnaireKind = z.infer<typeof paQuestionnaireKindSchema>;

export const paAnswerSchema = z.enum(['yes', 'no']);
export type PaAnswer = z.infer<typeof paAnswerSchema>;

export const paQuestionOutcomeSchema = z.enum(['pass', 'flag', 'deny', 'rationale_required']);
export type PaQuestionOutcome = z.infer<typeof paQuestionOutcomeSchema>;

export const paRuleKeySchema = z.enum([
    'restricted_list',
    'house_list',
    'manual_security',
    'questionnaire',
    'default_allow',
]);
export type PaRuleKey = z.infer<typeof paRuleKeySchema>;

export const paRuleOutcomeSchema = z.enum(['deny', 'ask_compliance', 'allow']);
export type PaRuleOutcome = z.infer<typeof paRuleOutcomeSchema>;

export const paRuleResultStateSchema = z.enum(['passed', 'fired', 'skipped_disabled']);
export type PaRuleResultState = z.infer<typeof paRuleResultStateSchema>;

export const paEvaluationOutcomeSchema = z.enum(['deny', 'compliance_review', 'allow']);
export type PaEvaluationOutcome = z.infer<typeof paEvaluationOutcomeSchema>;

export const paReasonCodeSchema = z.enum([
    'RESTRICTED_LIST_MATCH',
    'RESTRICTED_LIST_NAME_MATCH',
    'RESTRICTED_LIST_UNCERTAIN_MATCH',
    'HOUSE_LIST_SECURITY',
    'MANUAL_SECURITY',
    'NO_STABLE_IDENTIFIER',
    'HOUSE_STOCK_UNDER_3_MONTHS',
    'CLIENTS_NOT_BOUGHT_FIRST',
    'CLIENTS_NOT_SOLD_FIRST',
    'COPYING_EXECUTION_ONLY_ORDER',
    'POLICY_CONFIRMATION_FAILED',
    'DEFAULT_ALLOW',
    'DEFAULT_ALLOW_DISABLED',
]);
export type PaReasonCode = z.infer<typeof paReasonCodeSchema>;

export const paMatchBasisSchema = z.enum(['isin', 'sedol', 'cusip', 'name', 'uncertain']);
export type PaMatchBasis = z.infer<typeof paMatchBasisSchema>;

export const paCommentVisibilitySchema = z.enum(['employee_visible', 'internal']);
export type PaCommentVisibility = z.infer<typeof paCommentVisibilitySchema>;

export const paTradeRequestEventTypeSchema = z.enum([
    'created',
    'edited',
    'submitted',
    'status_changed',
    'information_requested',
    'information_provided',
    'comment_added',
    'document_attached',
    'clearance_extended',
    'comment_visibility_changed',
]);
export type PaTradeRequestEventType = z.infer<typeof paTradeRequestEventTypeSchema>;

export const paRestrictionChangeKindSchema = z.enum([
    'created',
    'updated',
    'disabled',
    'enabled',
    'cloned',
    'deleted',
    /** The expiry sweep recorded that the expiration date passed (SEP-271). */
    'expired',
]);
export type PaRestrictionChangeKind = z.infer<typeof paRestrictionChangeKindSchema>;

/** The persona tokens a restriction can name. The same set as `users.personas`. */
export const paPersonaGroupSchema = z.enum([
    'desk',
    'im-assistant',
    'ops',
    'comply',
    'view',
    'admin',
]);
export type PaPersonaGroup = z.infer<typeof paPersonaGroupSchema>;

/** The common ISO 4217 codes the form offers. `other` lets the employee type one. */
export const PA_COMMON_CURRENCIES = [
    'GBP',
    'USD',
    'EUR',
    'CHF',
    'JPY',
    'CAD',
    'AUD',
    'HKD',
    'SEK',
    'NOK',
    'DKK',
] as const;

/** The one sentence an employee sees on a blocked request. */
export const PA_RESTRICTED_NOTICE = 'This security is on the Restricted List. Contact Compliance.';

// ---------------------------------------------------------------------------
// The request body: the trade fields.
// ---------------------------------------------------------------------------

export const paTradeFieldsBodySchema = z.object({
    transactionType: paTransactionTypeSchema,
    side: paSideSchema,
    securityType: paSecurityTypeSchema,
    /** Units. Positive. */
    quantity: z.number().positive(),
    estimatedValue: z.number().nonnegative().nullable().optional(),
    /** ISO 4217. */
    currency: z.string().length(3),
    /** Required when `transactionType` is `limit`. */
    limitPrice: z.number().positive().nullable().optional(),
    /** A plain calendar date. Required when `transactionType` is `limit`. */
    goodUntil: z.string().nullable().optional(),
    beneficialOwner: paBeneficialOwnerSchema,
    ownerName: z.string().nullable().optional(),
    relationship: z.string().nullable().optional(),
    accountDetails: z.string().nullable().optional(),
    rationale: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    securityName: z.string().min(1),
    isin: z.string().nullable().optional(),
    sedol: z.string().nullable().optional(),
    cusip: z.string().nullable().optional(),
    ticker: z.string().nullable().optional(),
    figi: z.string().nullable().optional(),
    exchange: z.string().nullable().optional(),
    /**
     * The `pickToken` of the search result the employee picked, or null for a
     * security typed by hand. The SERVER decides the security source: a token
     * that it signed for these same identifiers makes the source `search`, and
     * anything else makes it `manual` (review H1). No client names the source.
     */
    securityPickToken: z.string().nullable().optional(),
    /** The answers so far, keyed by question key. */
    answers: z.record(z.string(), paAnswerSchema).optional(),
});
export type PaTradeFieldsBody = z.infer<typeof paTradeFieldsBodySchema>;

export const updatePaTradeRequestBodySchema = paTradeFieldsBodySchema.partial();
export type UpdatePaTradeRequestBody = z.infer<typeof updatePaTradeRequestBodySchema>;

// ---------------------------------------------------------------------------
// The snapshot parts.
// ---------------------------------------------------------------------------

export const paQuestionShownSchema = z.object({
    key: z.string(),
    wording: z.string(),
    version: z.number().int(),
});
export type PaQuestionShown = z.infer<typeof paQuestionShownSchema>;

export const paQuestionFlagSchema = z.object({
    questionKey: z.string(),
    answer: paAnswerSchema,
    outcome: paQuestionOutcomeSchema,
    reasonCode: paReasonCodeSchema,
});
export type PaQuestionFlag = z.infer<typeof paQuestionFlagSchema>;

export const paRuleConfigSnapshotSchema = z.object({
    ruleKey: paRuleKeySchema,
    name: z.string(),
    enabled: z.boolean(),
    version: z.number().int(),
});
export type PaRuleConfigSnapshot = z.infer<typeof paRuleConfigSnapshotSchema>;

export const paRuleResultSchema = z.object({
    ruleKey: paRuleKeySchema,
    state: paRuleResultStateSchema,
    outcome: paRuleOutcomeSchema.nullable(),
    reasonCodes: z.array(paReasonCodeSchema),
    detail: z.string(),
});
export type PaRuleResult = z.infer<typeof paRuleResultSchema>;

/** Compliance only: one matched restriction, with its name joined for display. */
export const paMatchEvidenceSchema = z.object({
    restrictionId: z.string(),
    /** Null when the restriction was hard-deleted since. */
    restrictionSecurityName: z.string().nullable(),
    projectName: z.string().nullable(),
    basis: paMatchBasisSchema,
    identifiersUsed: z.record(z.string(), z.string()),
    reasonCode: paReasonCodeSchema,
});
export type PaMatchEvidence = z.infer<typeof paMatchEvidenceSchema>;

// ---------------------------------------------------------------------------
// Comments, documents, events.
// ---------------------------------------------------------------------------

export const paCommentSchema = z.object({
    id: z.string(),
    authorUserId: z.string(),
    authorName: z.string().nullable(),
    /** True when the author holds `comply` or `admin`. */
    authorIsCompliance: z.boolean(),
    body: z.string(),
    visibility: paCommentVisibilitySchema,
    createdAt: z.string(),
});
export type PaComment = z.infer<typeof paCommentSchema>;

export const paDocumentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    contentType: z.string().nullable(),
    sizeBytes: z.number().int(),
    uploadedByUserId: z.string(),
    uploadedByName: z.string().nullable(),
    uploadedAt: z.string(),
    /** The path that streams the bytes, relative to the API origin. */
    downloadPath: z.string(),
});
export type PaDocument = z.infer<typeof paDocumentSchema>;

export const paEventSchema = z.object({
    id: z.string(),
    eventType: paTradeRequestEventTypeSchema,
    fromStatus: paTradeRequestStatusSchema.nullable(),
    toStatus: paTradeRequestStatusSchema.nullable(),
    actorUserId: z.string().nullable(),
    actorName: z.string().nullable(),
    actorPersona: z.string().nullable(),
    at: z.string(),
    note: z.string().nullable(),
});
export type PaEvent = z.infer<typeof paEventSchema>;

// ---------------------------------------------------------------------------
// The employee shape.
// ---------------------------------------------------------------------------

const tradeFieldsDtoShape = {
    transactionType: paTransactionTypeSchema,
    side: paSideSchema,
    securityType: paSecurityTypeSchema,
    quantity: z.number(),
    estimatedValue: z.number().nullable(),
    currency: z.string(),
    limitPrice: z.number().nullable(),
    goodUntil: z.string().nullable(),
    beneficialOwner: paBeneficialOwnerSchema,
    ownerName: z.string().nullable(),
    relationship: z.string().nullable(),
    accountDetails: z.string().nullable(),
    rationale: z.string().nullable(),
    notes: z.string().nullable(),
    securityName: z.string(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    cusip: z.string().nullable(),
    ticker: z.string().nullable(),
    figi: z.string().nullable(),
    exchange: z.string().nullable(),
    securitySource: paSecuritySourceSchema,
};

/** One row of "My requests". */
export const myPaTradeRequestSummarySchema = z.object({
    id: z.string(),
    status: paTradeRequestStatusSchema,
    /**
     * The status as of now: `expired` when a live clearance's instant has
     * passed and the hourly sweep has not moved the row yet. Equal to
     * `status` otherwise.
     */
    effectiveStatus: paTradeRequestStatusSchema,
    transactionType: paTransactionTypeSchema,
    side: paSideSchema,
    securityName: z.string(),
    isin: z.string().nullable(),
    ticker: z.string().nullable(),
    quantity: z.number(),
    currency: z.string(),
    estimatedValue: z.number().nullable(),
    createdAt: z.string(),
    submittedAt: z.string().nullable(),
    clearanceValidUntil: z.string().nullable(),
    lastEditedAt: z.string(),
    /** The note of the Compliance decision (approve or reject), or null. */
    decisionNote: z.string().nullable(),
    /** The instant of the Compliance decision, or null. */
    reviewedAt: z.string().nullable(),
    /** The message of the latest information request, or null when there is none. */
    informationRequest: z.string().nullable(),
});
export type MyPaTradeRequestSummary = z.infer<typeof myPaTradeRequestSummarySchema>;

export const myPaTradeRequestsResponseSchema = z.object({
    requests: z.array(myPaTradeRequestSummarySchema),
});
export type MyPaTradeRequestsResponse = z.infer<typeof myPaTradeRequestsResponseSchema>;

/** The request as its owner sees it. */
export const myPaTradeRequestSchema = z.object({
    id: z.string(),
    status: paTradeRequestStatusSchema,
    effectiveStatus: paTradeRequestStatusSchema,
    ...tradeFieldsDtoShape,
    createdAt: z.string(),
    lastEditedAt: z.string(),
    submittedAt: z.string().nullable(),
    questionnaireKind: paQuestionnaireKindSchema.nullable(),
    questionsShown: z.array(paQuestionShownSchema),
    answers: z.record(z.string(), paAnswerSchema),
    /** The flags the employee's own answers raised. */
    flags: z.array(paQuestionFlagSchema),
    houseListMatch: z.boolean(),
    /** The one sentence for a blocked request, or null. */
    blockedNotice: z.string().nullable(),
    clearanceGrantedAt: z.string().nullable(),
    clearanceValidUntil: z.string().nullable(),
    clearanceExtendedReason: z.string().nullable(),
    decisionNote: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    withdrawnReason: z.string().nullable(),
    /** Employee-visible comments only. */
    comments: z.array(paCommentSchema),
    documents: z.array(paDocumentSchema),
    /** The timeline without the Compliance-only rows. */
    events: z.array(paEventSchema),
    canEdit: z.boolean(),
    canSubmit: z.boolean(),
    canWithdraw: z.boolean(),
    canRespond: z.boolean(),
});
export type MyPaTradeRequest = z.infer<typeof myPaTradeRequestSchema>;

/** The questionnaire the caller answers, resolved for their kind and the side. */
export const paQuestionnaireQuestionSchema = z.object({
    key: z.string(),
    wording: z.string(),
    version: z.number().int(),
    /** The answer that fires the outcome. */
    firesOn: paAnswerSchema,
    /** True when a fired answer needs a rationale before the submission. */
    rationaleRequiredWhenFired: z.boolean(),
});
export type PaQuestionnaireQuestion = z.infer<typeof paQuestionnaireQuestionSchema>;

export const myPaQuestionnaireResponseSchema = z.object({
    questionnaireKind: paQuestionnaireKindSchema,
    questions: z.array(paQuestionnaireQuestionSchema),
    /** True when the identifiers in the query match the house list. */
    houseListMatch: z.boolean(),
    houseListHint: z.string().nullable(),
});
export type MyPaQuestionnaireResponse = z.infer<typeof myPaQuestionnaireResponseSchema>;

export const myPaQuestionnaireQuerySchema = z.object({
    side: paSideSchema,
    isin: z.string().optional(),
    sedol: z.string().optional(),
});
export type MyPaQuestionnaireQuery = z.infer<typeof myPaQuestionnaireQuerySchema>;

/** One security search result. */
export const paSecuritySearchResultSchema = z.object({
    name: z.string(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    cusip: z.string().nullable(),
    ticker: z.string().nullable(),
    figi: z.string().nullable(),
    exchange: z.string().nullable(),
    /** OpenFIGI's market sector or the local type, for a hint. */
    marketSector: z.string().nullable(),
    source: z.enum(['local', 'openfigi']),
    onHouseList: z.boolean(),
    /**
     * An opaque token the server signs over the name and the identifiers of
     * this result. The form sends it back as `securityPickToken`, and the
     * server verifies it before it treats the security as a search pick.
     */
    pickToken: z.string(),
});
export type PaSecuritySearchResult = z.infer<typeof paSecuritySearchResultSchema>;

export const paSecuritySearchResponseSchema = z.object({
    results: z.array(paSecuritySearchResultSchema),
    /** True when OpenFIGI answered. False when it is unconfigured or down. */
    externalSearched: z.boolean(),
});
export type PaSecuritySearchResponse = z.infer<typeof paSecuritySearchResponseSchema>;

export const submitPaTradeRequestResponseSchema = z.object({
    id: z.string(),
    status: paTradeRequestStatusSchema,
    /** The flags the answers raised, for the confirmation screen. */
    flags: z.array(paQuestionFlagSchema),
    blockedNotice: z.string().nullable(),
    clearanceValidUntil: z.string().nullable(),
});
export type SubmitPaTradeRequestResponse = z.infer<typeof submitPaTradeRequestResponseSchema>;

export const paCommentBodySchema = z.object({
    body: z.string().min(1),
    /** Compliance only. An employee comment is always employee_visible. */
    visibility: paCommentVisibilitySchema.optional(),
});
export type PaCommentBody = z.infer<typeof paCommentBodySchema>;

export const respondToPaInformationRequestBodySchema = z.object({
    comment: z.string().nullable().optional(),
});
export type RespondToPaInformationRequestBody = z.infer<
    typeof respondToPaInformationRequestBodySchema
>;

export const withdrawPaTradeRequestBodySchema = z.object({
    reason: z.string().nullable().optional(),
});
export type WithdrawPaTradeRequestBody = z.infer<typeof withdrawPaTradeRequestBodySchema>;

// ---------------------------------------------------------------------------
// The per-user notification inbox. The schemas are in `user-notifications.ts`,
// because Transfers writes to the same inbox (SEP-317 section 7). This file
// gives them again, so each import of PA Dealing stays valid.
// ---------------------------------------------------------------------------

export {
    userNotificationKindSchema,
    userNotificationSchema,
    userNotificationsResponseSchema,
    userNotificationSubjectKindSchema,
    type UserNotification,
    type UserNotificationKind,
    type UserNotificationsResponse,
    type UserNotificationSubjectKind,
} from './user-notifications';

// ---------------------------------------------------------------------------
// The Compliance shape.
// ---------------------------------------------------------------------------

/** One row of the review queue. */
export const paTradeRequestReviewSummarySchema = z.object({
    id: z.string(),
    status: paTradeRequestStatusSchema,
    effectiveStatus: paTradeRequestStatusSchema,
    employeeUserId: z.string(),
    employeeName: z.string().nullable(),
    employeeRole: z.string().nullable(),
    employeePersonas: z.array(z.string()),
    questionnaireKind: paQuestionnaireKindSchema.nullable(),
    transactionType: paTransactionTypeSchema,
    side: paSideSchema,
    securityName: z.string(),
    isin: z.string().nullable(),
    ticker: z.string().nullable(),
    quantity: z.number(),
    currency: z.string(),
    estimatedValue: z.number().nullable(),
    outcome: paEvaluationOutcomeSchema.nullable(),
    reasonCodes: z.array(paReasonCodeSchema),
    restrictedListMatch: z.boolean(),
    createdAt: z.string(),
    submittedAt: z.string().nullable(),
    reviewedByUserId: z.string().nullable(),
    reviewedByName: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    clearanceValidUntil: z.string().nullable(),
});
export type PaTradeRequestReviewSummary = z.infer<typeof paTradeRequestReviewSummarySchema>;

/** The SEP-270 queue filters. Every one is optional. */
export const paReviewQueueQuerySchema = z.object({
    status: paTradeRequestStatusSchema.optional(),
    employeeUserId: z.string().optional(),
    /** A persona token, matched against the personas stored at submission. */
    employeeRole: z.string().optional(),
    questionnaireKind: paQuestionnaireKindSchema.optional(),
    transactionType: paTransactionTypeSchema.optional(),
    side: paSideSchema.optional(),
    /** Matched against the name, the ISIN, the SEDOL and the ticker. */
    security: z.string().optional(),
    quantityMin: z.coerce.number().optional(),
    quantityMax: z.coerce.number().optional(),
    /** Inclusive bounds on the submission date, as plain dates or instants. */
    requestDateFrom: z.string().optional(),
    requestDateTo: z.string().optional(),
    reasonCode: paReasonCodeSchema.optional(),
    outcome: paEvaluationOutcomeSchema.optional(),
    /** `true` keeps only requests that matched a restriction; `false` the others. */
    restrictedListMatch: z.enum(['true', 'false']).optional(),
    reviewedByUserId: z.string().optional(),
    decisionDateFrom: z.string().optional(),
    decisionDateTo: z.string().optional(),
    /** Free text over the employee name, the security and the notes. */
    search: z.string().optional(),
    /** Drafts are the employee's own, and the queue hides them by default. */
    includeDrafts: z.enum(['true', 'false']).optional(),
    limit: z.coerce.number().int().positive().max(500).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
export type PaReviewQueueQuery = z.infer<typeof paReviewQueueQuerySchema>;

export const paReviewQueueResponseSchema = z.object({
    rows: z.array(paTradeRequestReviewSummarySchema),
    total: z.number().int().nonnegative(),
    /** The count of every non-draft request by status, unfiltered. */
    countsByStatus: z.record(z.string(), z.number().int().nonnegative()),
});
export type PaReviewQueueResponse = z.infer<typeof paReviewQueueResponseSchema>;

/** The full record for Compliance. */
export const paTradeRequestReviewSchema = z.object({
    id: z.string(),
    status: paTradeRequestStatusSchema,
    effectiveStatus: paTradeRequestStatusSchema,
    employeeUserId: z.string(),
    employeeName: z.string().nullable(),
    employeeEmail: z.string().nullable(),
    employeeRole: z.string().nullable(),
    ...tradeFieldsDtoShape,
    createdAt: z.string(),
    lastEditedAt: z.string(),
    submittedAt: z.string().nullable(),
    employeePersonas: z.array(z.string()),
    questionnaireKind: paQuestionnaireKindSchema.nullable(),
    questionsShown: z.array(paQuestionShownSchema),
    answers: z.record(z.string(), paAnswerSchema),
    ruleConfigSnapshot: z.array(paRuleConfigSnapshotSchema),
    ruleResults: z.array(paRuleResultSchema),
    outcome: paEvaluationOutcomeSchema.nullable(),
    reasonCodes: z.array(paReasonCodeSchema),
    flags: z.array(paQuestionFlagSchema),
    matchEvidence: z.array(paMatchEvidenceSchema),
    houseListMatch: z.boolean(),
    engineVersion: z.string().nullable(),
    reviewedByUserId: z.string().nullable(),
    reviewedByName: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    clearanceGrantedAt: z.string().nullable(),
    clearanceValidUntil: z.string().nullable(),
    clearanceExtendedReason: z.string().nullable(),
    withdrawnByUserId: z.string().nullable(),
    withdrawnReason: z.string().nullable(),
    expiredAt: z.string().nullable(),
    /** Every comment, internal ones included. */
    comments: z.array(paCommentSchema),
    documents: z.array(paDocumentSchema),
    events: z.array(paEventSchema),
    canApprove: z.boolean(),
    canReject: z.boolean(),
    canRequestInformation: z.boolean(),
    canWithdraw: z.boolean(),
    canExtendClearance: z.boolean(),
    /**
     * True when the reader owns the request. Another Compliance officer must
     * decide it, so every decision flag above is false (review C2).
     */
    selfReview: z.boolean(),
});
export type PaTradeRequestReview = z.infer<typeof paTradeRequestReviewSchema>;

export const paDecisionBodySchema = z.object({ note: z.string().nullable().optional() });
export type PaDecisionBody = z.infer<typeof paDecisionBodySchema>;

export const paRejectBodySchema = z.object({ note: z.string().min(1) });
export type PaRejectBody = z.infer<typeof paRejectBodySchema>;

export const paRequestInformationBodySchema = z.object({ message: z.string().min(1) });
export type PaRequestInformationBody = z.infer<typeof paRequestInformationBodySchema>;

export const paComplianceWithdrawBodySchema = z.object({ reason: z.string().min(1) });
export type PaComplianceWithdrawBody = z.infer<typeof paComplianceWithdrawBodySchema>;

export const paExtendClearanceBodySchema = z.object({
    /** A plain calendar date. The clearance ends at the end of that London day. */
    validUntilDate: z.string(),
    reason: z.string().min(1),
});
export type PaExtendClearanceBody = z.infer<typeof paExtendClearanceBodySchema>;

export const paCommentVisibilityBodySchema = z.object({ visibility: paCommentVisibilitySchema });
export type PaCommentVisibilityBody = z.infer<typeof paCommentVisibilityBodySchema>;

// --- The Restricted List ---

export const paRestrictionSchema = z.object({
    id: z.string(),
    securityName: z.string(),
    ticker: z.string().nullable(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    cusip: z.string().nullable(),
    securityType: paSecurityTypeSchema.nullable(),
    industry: z.string().nullable(),
    projectName: z.string().nullable(),
    comments: z.string().nullable(),
    restrictedGroups: z.array(z.string()),
    restrictedUserIds: z.array(z.string()),
    /** Display names, index-aligned with `restrictedUserIds`. */
    restrictedUserNames: z.array(z.string().nullable()),
    effectiveDate: z.string(),
    expirationDate: z.string().nullable(),
    monitoredByUserId: z.string().nullable(),
    monitoredByName: z.string().nullable(),
    dealId: z.string().nullable(),
    otherIdentifiers: z.string().nullable(),
    enabled: z.boolean(),
    /** True when enabled and inside the date window today. */
    active: z.boolean(),
    /** True when the expiration date has passed. */
    expired: z.boolean(),
    disabledAt: z.string().nullable(),
    disabledByUserId: z.string().nullable(),
    disabledByName: z.string().nullable(),
    disabledReason: z.string().nullable(),
    version: z.number().int(),
    createdAt: z.string(),
    createdByUserId: z.string().nullable(),
    createdByName: z.string().nullable(),
    lastEditedAt: z.string(),
    lastEditedByUserId: z.string().nullable(),
    lastEditedByName: z.string().nullable(),
    clonedFromId: z.string().nullable(),
    /** The count of requests that ever matched. Non-zero refuses a delete. */
    matchCount: z.number().int().nonnegative(),
});
export type PaRestriction = z.infer<typeof paRestrictionSchema>;

export const paRestrictionFieldsBodySchema = z.object({
    securityName: z.string().min(1),
    ticker: z.string().nullable().optional(),
    isin: z.string().nullable().optional(),
    sedol: z.string().nullable().optional(),
    cusip: z.string().nullable().optional(),
    securityType: paSecurityTypeSchema.nullable().optional(),
    industry: z.string().nullable().optional(),
    projectName: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
    restrictedGroups: z.array(paPersonaGroupSchema).optional(),
    restrictedUserIds: z.array(z.string()).optional(),
    effectiveDate: z.string(),
    expirationDate: z.string().nullable().optional(),
    monitoredByUserId: z.string().nullable().optional(),
    dealId: z.string().nullable().optional(),
    otherIdentifiers: z.string().nullable().optional(),
});
export type PaRestrictionFieldsBody = z.infer<typeof paRestrictionFieldsBodySchema>;

export const updatePaRestrictionBodySchema = paRestrictionFieldsBodySchema.partial().extend({
    reason: z.string().nullable().optional(),
});
export type UpdatePaRestrictionBody = z.infer<typeof updatePaRestrictionBodySchema>;

export const clonePaRestrictionBodySchema = paRestrictionFieldsBodySchema.partial();
export type ClonePaRestrictionBody = z.infer<typeof clonePaRestrictionBodySchema>;

export const paReasonBodySchema = z.object({ reason: z.string().min(1) });
export type PaReasonBody = z.infer<typeof paReasonBodySchema>;

export const paOptionalReasonBodySchema = z.object({ reason: z.string().nullable().optional() });
export type PaOptionalReasonBody = z.infer<typeof paOptionalReasonBodySchema>;

export const paRestrictionsQuerySchema = z.object({
    /** `true` includes expired and disabled rows. The default hides them. */
    includeInactive: z.enum(['true', 'false']).optional(),
    search: z.string().optional(),
});
export type PaRestrictionsQuery = z.infer<typeof paRestrictionsQuerySchema>;

export const paRestrictionsResponseSchema = z.object({
    restrictions: z.array(paRestrictionSchema),
    /** The count of rows the `includeInactive` default hid. */
    hiddenCount: z.number().int().nonnegative(),
});
export type PaRestrictionsResponse = z.infer<typeof paRestrictionsResponseSchema>;

export const paRestrictionVersionSchema = z.object({
    id: z.string(),
    restrictionId: z.string(),
    version: z.number().int(),
    changeKind: paRestrictionChangeKindSchema,
    changedByUserId: z.string().nullable(),
    changedByName: z.string().nullable(),
    changedAt: z.string(),
    reason: z.string().nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
});
export type PaRestrictionVersion = z.infer<typeof paRestrictionVersionSchema>;

export const paRestrictionDetailResponseSchema = z.object({
    restriction: paRestrictionSchema,
    versions: z.array(paRestrictionVersionSchema),
});
export type PaRestrictionDetailResponse = z.infer<typeof paRestrictionDetailResponseSchema>;

export const paRestrictionVersionsResponseSchema = z.object({
    versions: z.array(paRestrictionVersionSchema),
});
export type PaRestrictionVersionsResponse = z.infer<typeof paRestrictionVersionsResponseSchema>;

// --- The rules and the questions ---

export const paRuleSchema = z.object({
    id: z.string(),
    ruleKey: paRuleKeySchema,
    name: z.string(),
    category: z.string(),
    ruleOutcome: paRuleOutcomeSchema,
    sortOrder: z.number().int(),
    enabled: z.boolean(),
    version: z.number().int(),
    lastChangedByUserId: z.string().nullable(),
    lastChangedByName: z.string().nullable(),
    lastChangedAt: z.string().nullable(),
    lastChangeReason: z.string().nullable(),
});
export type PaRule = z.infer<typeof paRuleSchema>;

export const paRulesResponseSchema = z.object({ rules: z.array(paRuleSchema) });
export type PaRulesResponse = z.infer<typeof paRulesResponseSchema>;

export const paToggleBodySchema = z.object({ enabled: z.boolean(), reason: z.string().min(1) });
export type PaToggleBody = z.infer<typeof paToggleBodySchema>;

/** One question with its settings for both kinds. */
export const paQuestionSettingSchema = z.object({
    /** `<kind>:<questionKey>`, the id the toggle takes. */
    id: z.string(),
    questionKey: z.string(),
    questionnaireKind: paQuestionnaireKindSchema,
    wording: z.string(),
    version: z.number().int(),
    appliesToSide: paSideSchema.nullable(),
    firesOn: paAnswerSchema,
    outcome: paQuestionOutcomeSchema,
    reasonCode: paReasonCodeSchema,
    rationaleRequiredWhenFired: z.boolean(),
    /** True when Compliance cannot switch the question off (SEP-268). */
    alwaysOn: z.boolean(),
    enabled: z.boolean(),
    settingVersion: z.number().int(),
    lastChangedByUserId: z.string().nullable(),
    lastChangedByName: z.string().nullable(),
    lastChangedAt: z.string().nullable(),
    lastChangeReason: z.string().nullable(),
});
export type PaQuestionSetting = z.infer<typeof paQuestionSettingSchema>;

export const paQuestionSettingsResponseSchema = z.object({
    settings: z.array(paQuestionSettingSchema),
});
export type PaQuestionSettingsResponse = z.infer<typeof paQuestionSettingsResponseSchema>;

// --- The questionnaire assignments ---

export const paQuestionnaireAssignmentSchema = z.object({
    userId: z.string(),
    name: z.string().nullable(),
    email: z.string(),
    role: z.string().nullable(),
    personas: z.array(z.string()),
    /** The kind the personas imply. */
    defaultKind: paQuestionnaireKindSchema,
    /** The Compliance override, or null. */
    overrideKind: paQuestionnaireKindSchema.nullable(),
    /** The kind that applies: the override when set, else the default. */
    effectiveKind: paQuestionnaireKindSchema,
    overrideSetByName: z.string().nullable(),
    overrideSetAt: z.string().nullable(),
    overrideReason: z.string().nullable(),
});
export type PaQuestionnaireAssignment = z.infer<typeof paQuestionnaireAssignmentSchema>;

export const paQuestionnaireAssignmentsResponseSchema = z.object({
    assignments: z.array(paQuestionnaireAssignmentSchema),
});
export type PaQuestionnaireAssignmentsResponse = z.infer<
    typeof paQuestionnaireAssignmentsResponseSchema
>;

export const setPaQuestionnaireAssignmentBodySchema = z.object({
    /** Null clears the override. */
    questionnaireKind: paQuestionnaireKindSchema.nullable(),
    reason: z.string().nullable().optional(),
});
export type SetPaQuestionnaireAssignmentBody = z.infer<
    typeof setPaQuestionnaireAssignmentBodySchema
>;

/** A staff member for a picker. */
export const paStaffMemberSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string(),
    role: z.string().nullable(),
    personas: z.array(z.string()),
    /**
     * True when the server accepts this user as the "Monitored By" user of a
     * restriction (SEP-266). The picker offers these users only.
     */
    canMonitorRestrictions: z.boolean(),
});
export type PaStaffMember = z.infer<typeof paStaffMemberSchema>;

export const paStaffResponseSchema = z.object({ staff: z.array(paStaffMemberSchema) });
export type PaStaffResponse = z.infer<typeof paStaffResponseSchema>;

// --- The audit search ---

export const paAuditQuerySchema = z.object({
    employeeUserId: z.string().optional(),
    /** Matched against the security name and the ISIN in the event metadata. */
    security: z.string().optional(),
    status: paTradeRequestStatusSchema.optional(),
    reasonCode: paReasonCodeSchema.optional(),
    /** The action key, for example `approve-pa-trade-request`. */
    eventType: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    limit: z.coerce.number().int().positive().max(1000).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
export type PaAuditQuery = z.infer<typeof paAuditQuerySchema>;

export const paAuditRowSchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    actionKey: z.string(),
    actorUserId: z.string().nullable(),
    actorName: z.string().nullable(),
    actorPersona: z.string().nullable(),
    targetType: z.string().nullable(),
    targetId: z.string().nullable(),
    /** The employee the event is about, when the event names one. */
    employeeUserId: z.string().nullable(),
    employeeName: z.string().nullable(),
    securityName: z.string().nullable(),
    status: z.string().nullable(),
    reasonCodes: z.array(z.string()),
    summary: z.string().nullable(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
});
export type PaAuditRow = z.infer<typeof paAuditRowSchema>;

export const paAuditResponseSchema = z.object({
    rows: z.array(paAuditRowSchema),
    total: z.number().int().nonnegative(),
    /** The action keys present, for the event type filter. */
    eventTypes: z.array(z.string()),
});
export type PaAuditResponse = z.infer<typeof paAuditResponseSchema>;
